import { useMemo } from 'react';
import { NextPageWithLayout } from '~/pages/_app';
import { useRouter } from 'next/router';
import { pipe } from 'fp-ts/lib/function';
import {
  chain,
  fromNullable,
  fromPredicate,
  map,
  match,
  toUndefined,
} from 'fp-ts/lib/Option';
import { PressReleaseItem } from '@oresundsbron/api/src/backend/PressRelease';
import { useLocale } from '~/stores/locale';
import { useSinglePressItem } from '~/hooks/PressRelease/useSinglePressItem';
import NotFound from '../NotFound';
import { createSeoMetaData } from './utils';
import { SEOMetadata } from '~/components/SEOMetadata';
import { useContactInformation } from '~/hooks/PressRelease/useContactInformation';
import { SingleNewsPageContent } from './SingleNewsPageContent';
import { LoadingScreen } from '@oresundsbron/app-common';

const SingleNewsPage: NextPageWithLayout = () => {
  const router = useRouter();
  const { asPath } = router;
  const { id, query } = router.query;
  const { locale } = useLocale();

  const { data, error, isFetching } = useSinglePressItem(
    parseInt(id as string)
  );

  const { data: contentfulContactInfo } = useContactInformation();

  const singleNewsInfo = useMemo(
    () =>
      pipe(
        data,
        fromNullable,
        map((n) => n as PressReleaseItem),
        toUndefined
      ),
    [data]
  );

  if (isFetching) {
    return <LoadingScreen />;
  }

  if (error || !singleNewsInfo) {
    return <NotFound />;
  }

  return pipe(
    singleNewsInfo?.url,
    fromNullable,
    chain(fromPredicate((u) => u.includes(query as string))),
    match(
      () => <NotFound />,
      () => (
        <>
          <SEOMetadata items={createSeoMetaData(asPath, singleNewsInfo)} />

          <SingleNewsPageContent
            singleNewsInfo={singleNewsInfo}
            locale={locale}
            contentfulContactInfo={contentfulContactInfo}
          />
        </>
      )
    )
  );
};

export default SingleNewsPage;
